import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { FirebaseRemoteConfigService, SentryErrorHandler } from '@core/services/common';
import { BusinessesPasswordRecoveryContextService } from '@core/services/context/businesses-password-recovery-context.service';
import { NonRenewalContextService } from '@core/services/context/non-renewal-context.service';
import { ContextService } from '@core/services/core/context.service';
import { MMobileHelperService } from '@core/services/core/mmobile-helper.service';
import { NavigationHelper } from '@core/services/core/navigation-helper.service';
import { GenesysChatHelperService } from '@core/services/helpers/genesys-chat-helper.service';
import { PatrimonialDataChangeHelperService } from '@core/services/helpers/patrimonial-data-change-helper.service';
import { PaymentPlanService } from '@core/services/http-services/payment-plan.service';
import { Keyboard } from '@ionic-native/keyboard/ngx';
import { Platform } from '@ionic/angular';
import { PensionPlanHelperService } from '@modules/pension-plan/services/pension-plan-helper.service';
import { BehaviorSubject, defer, from, fromEvent, merge, Observable, of, throwError, timer } from 'rxjs';

import {
  catchError,
  distinctUntilKeyChanged,
  filter,
  finalize,
  first,
  map,
  pluck,
  share,
  skipUntil,
  startWith,
  switchMap,
  switchMapTo,
  take,
  tap,
  throttleTime,
  timeout
} from 'rxjs/operators';
import { User, UserDocumentType, WILLMANAGEMENT_DOCUMENTS } from 'src/app/core/models/user';
import { isEmpty, joinStrings, padZeros } from '../../../utils/utils';
import { DocumentCaptureContextService } from '../../context/document-capture-context.service';
import { PaymentPlanContextService } from '../../context/payment-plan-context.service';
import { PoliciesContextService } from '../../context/policies-context.service';
import { ProfileContextService } from '../../context/profile-context.service';
import { NotificationHelperService } from '../../helpers/notification-helper.service';
import { AuthHttpService } from '../../http-services/auth.http';
import { AnalyticsContext } from '../analytics/analytics-context';
import { ExternalUrlOpenerHelperService } from '../external-url-opener-helper.service';
import { LogService } from '../log.service';
import { MenuService } from '../menu.service';
import { SecureShareService } from '../secure-share.service';
import { KeychainService } from '../storage/keychain.service';
import { SecureStoreService } from '../storage/secure.service';
import { TecuidamosService } from '../tecuidamos.service';
import { AuthMirrorService } from './auth-mirror.service';
import {
  AppAuthPreferences,
  Auth,
  AuthAction,
  AuthType,
  BusinessCertificateLoginPayload,
  BusinessLoginPayload, LoginPayload,
  Logout,
  LogoutPayload,
  SetAuthenticationPayload,
  UpdateLegalRepresentativePayload,
  UpdatePasswordPayload,
  UpdatePersonalDataPayload,
  UpdatePhonePayload
} from './auth.actions';
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private jwt = new JwtHelperService();
  private serviceReadySource = new BehaviorSubject(false);
  private ready$ = this.serviceReadySource.pipe(filter(Boolean), first());

  private readonly initialState: Auth = { isLogged: false };
  private state = new BehaviorSubject<Auth>(this.initialState);

  constructor(
    private authHttp: AuthHttpService,
    private secure: SecureStoreService,
    private teCuidamos: TecuidamosService,
    private platform: Platform,
    private log: LogService,
    private keychain: KeychainService,
    private authMirror: AuthMirrorService,
    private analyticsContext: AnalyticsContext,
    private menu: MenuService,
    private policiesCtx: PoliciesContextService,
    private passwordRecoveryCtx: BusinessesPasswordRecoveryContextService,
    private paymentPlanCtx: PaymentPlanContextService,
    private profileCtx: ProfileContextService,
    private documentCaptureCtx: DocumentCaptureContextService,
    private keyboard: Keyboard,
    private urlExternal: ExternalUrlOpenerHelperService,
    private secureShare: SecureShareService,
    private notificationHelper: NotificationHelperService,
    private patrimonialDataChangeHelper: PatrimonialDataChangeHelperService,
    private nonRenewalContext: NonRenewalContextService,
    private mmobileHelper: MMobileHelperService,
    private pensionPlanHelper: PensionPlanHelperService,
    public ctx: ContextService,
    private paymentPlanService: PaymentPlanService,
    private sentry: SentryErrorHandler,
    private nav: NavigationHelper,
    private genesysHelper: GenesysChatHelperService,
    private firebaseConfig: FirebaseRemoteConfigService
  ) {
    this.authMirror.authService = this; // Circular dependency hack
    (this.platform.is('hybrid') ? this.initApp() : this.initWeb())
      .pipe(
        filter(Boolean),
        finalize(() => this.serviceReadySource.next(true))
      )
      .subscribe((stored: Auth) =>
        this.state.next({ ...this.getState(), ...stored })
      );

    this.state
      .pipe(startWith({} as Auth), distinctUntilKeyChanged('isLogged'))
      .subscribe(({ user, isLogged }) => {
        if (isLogged) {
          this.initializeInactivityLogout();
          this.sentry.setSentryUser({ id: user.userCode });
        } else {
          this.sentry.sentryConfigureScope(scope => scope.setUser(null));
        }
      });
  }

  private initApp() {
    return from(this.secure.getNotSecure<AppAuthPreferences>('AUTH__APP_PREFERENCES')).pipe(
      tap(() => this.log.d(this, 'Initialization: preferences are ready!')),
      map(preferences => ({ preferences } as Auth))
    );
  }

  private initWeb() {
    return from(this.secure.get<Auth>('AUTH__STATE'));
  }

  getState() {
    return this.state.getValue();
  }
  getState$() {
    return this.state.pipe(skipUntil(this.ready$), share());
  }
  getDocumentNumber() {
    const { user } = this.authMirror.authService.getState();
    const { documentNumber } = user || ({} as User);

    return documentNumber;
  }

  checkUserIsCompany() {
    const { user } = this.getState();
    return user && this.checkDocumentTypeIsCompany(user.documentType);
  }

  checkUserIsParticular() {
    const { user } = this.getState();
    return user && this.checkDocumentTypeIsParticular(user.documentType);
  }

  checkDocumentTypeIsCompany(documentType: UserDocumentType) {
    return UserDocumentType.CIF === documentType;
  }

  checkDocumentTypeIsParticular(documentType: UserDocumentType) {
    return UserDocumentType.CIF !== documentType;
  }

  checkUserHasInternalNetwork$(user: User) {
    return this.authHttp.getUserNetwork(user);
  }

  getConcatenatedUserIds() {
    const { user } = this.state.getValue() || {};
    if (!user) { return ''; }

    const userIdsList = [user.userId || '', ...user.additionalUserIds || []];
    return joinStrings(userIdsList, ' - ');
  }

  decodeTokenSafe(token: string) {
    try {
      return this.jwt.decodeToken(token);
    } catch (e) {
      this.log.w(this, e?.message);
      return null;
    }
  }

  dispatch(action: AuthAction): void {
    this.ready$.pipe(switchMap(() => this.innerDispatch(action))).subscribe(
      state => this.state.next(state),
      () => {}
    );
  }

  dispatch$(action: AuthAction): Observable<Auth> {
    return this.ready$.pipe(
      switchMap(() => this.innerDispatch(action)),
      tap(state => this.state.next(state))
    );
  }

  refreshAccessToken(jwtRefreshToken: string) {
    return this.authHttp.refreshAccessToken(jwtRefreshToken);
  }

  async isAppLoginTimeout() {
    const { isLogged, user } = await this.getState$().pipe(first()).toPromise();
    if (!isLogged || !user) {
      return false;
    }
    return (this.jwt.isTokenExpired(user.token) && this.jwt.isTokenExpired(user?.refreshToken));
  }

  private innerDispatch(action: AuthAction) {
    const { payload } = action;
    let res: Observable<Auth>;
    switch (action.type) {
      case 'LOGIN':
        const loginPayload: LoginPayload = action.payload; // Typing purposes
        const { type } = loginPayload;
        if (type === AuthType.FACE || type === AuthType.FINGERPRINT) {
          res = this.loginBiometric(type);
        } else if (type === AuthType.TOKEN) {
          res = this.loginToken(payload);
        } else if (type === AuthType.ACI) {
          res = this.loginAci(payload);
        } else {
          res = this.login(payload, type);
        }
        break;
      case 'BUSINESS_LOGIN':
        const blPayload: BusinessLoginPayload = action.payload;
        res = this.loginBusiness(blPayload);
        break;
      case 'BUSINESS_CERTIFICATE_LOGIN':
        res = this.loginBusinessCertificateToken(action.payload);
        break;
      case 'SET_AUTHENTICATION':
        res = this.setAppAuthenticationType(payload);
        break;
      case 'LOGOUT':
        const softLogout: LogoutPayload =
          action.payload && action.payload.softLogout;
          sessionStorage.removeItem('tokenAFIN');
          res = softLogout ? this.softLogout() : this.logout();
        break;
      case 'INTERCEPT_TOKEN':
        res = this.interceptToken(payload);
        break;
      case 'ACCEPT_RGPDUE':
        res = this.acceptRgpdue();
        break;
      case 'UPDATE_PASSWORD':
        res = this.updatePassword(payload);
        break;
      case 'UPDATE_PERSONAL_DATA':
        res = this.updatePersonalData(payload);
        break;
      case 'UPDATE_PHONE':
        res = this.updatePhone(payload);
        break;
      case 'UPDATE_LEGAL_REPRESENTATIVE':
        res = this.updateLegalRepresentative(payload);
        break;
      case 'UPDATE_COMPANY_PHONE':
        res = this.updateCompanyPhone(payload);
        break;
      default:
        break;
    }
    return res.pipe(
      take(1),
      tap(state => this.log.d(this, 'AUTH ACTION: ', action, state))
    );
  }

  private login(
    {
      documentNumber,
      password,
      nuumaSim,
    }: { documentNumber: string; password: string; nuumaSim?: string },
    type: AuthType
  ) {
    return from(this.checkSdmDocumentNumberList(documentNumber)).pipe(
      switchMap(allowSdmLogin => {
        if (allowSdmLogin && !nuumaSim) {
          return this.getLoginToken({ documentNumber, password }, type).pipe(
            switchMap(({ user }) => {
              return this.authHttp.getSdmUserProfile().pipe(
                map(userData =>
                  ({
                    ...this.getState(),
                    isLogged: !this.isAppFirstLogin() && userData.policyAccepted,
                    user: {
                      ...userData,
                      token: user.token,
                      userId: this.decodeTokenSafe(user.token)?.claims?.client?.userId,
                      mcaOnboarding: user?.mcaOnboarding
                    }
                  } as Auth)
                ),
                tap(state =>  this.state.next(state)),
                tap(state => this.storeState(state)),
                catchError(() => this.acceptRgpdue())
              );
          }));
        } else {
          return this.loginMets({ documentNumber, password, nuumaSim }, type);
        }
    }));
  }

  private getLoginToken(
    {
      documentNumber,
      password,
    }: { documentNumber: string; password: string; },
    type: AuthType
  ) {
    return this.authHttp.loginSdm({ documentNumber, password }, type).pipe(
      map(
        res =>
        ({
          ...this.getState(),
          sessionCounter: this.plusOneSessionCounter(),
          isLogged: !this.isAppFirstLogin(),
          password: this.platform.is('cordova') ? password : null,
          user: {
            token: res?.body?.token,
            mcaOnboarding: res?.body?.mca_onboarding,
            documentNumber: padZeros(documentNumber)
          }
        } as Auth)
      ),
      tap(({ sessionCounter }) =>
        this.log.d(this, 'NOTIFICATION SESSION COUNTER', '' + sessionCounter)
      ),
      tap(() => {
        if (!this.ctx.isApp) {this.loginAFIN(documentNumber, password); }
        this.secureShare.save({
          documentNumber: padZeros(documentNumber),
          password
        });
      }),
      tap(state =>  this.state.next(state)),
      tap(state => this.storeState(state)),
      tap(state => this.runBackgroundLoginJobs(state)),
      catchError((response: HttpErrorResponse) => this.handleSdmLoginErrors(response))
    );
  }

  private loginMets(
    {
      documentNumber,
      password,
      nuumaSim,
    }: { documentNumber; password; nuumaSim? },
    type: AuthType
  ) {
    const nuuma = this.getNuumaSim(nuumaSim);
    const loginResponse$ = nuuma
      ? this.authHttp.loginSim({ documentNumber, password, nuumaSim: nuuma })
      : this.authHttp.login({ documentNumber, password }, type);

    return loginResponse$.pipe(
      map(
        user =>
          ({
            ...this.getState(),
            sessionCounter: this.plusOneSessionCounter(),
            isLogged: !this.isAppFirstLogin() && user.policyAccepted,
            password: this.platform.is('cordova') ? password : null,
            user: {
              ...user,
              nuumaSim
            }
          } as Auth)
      ),
      tap(({ sessionCounter }) =>
        this.log.d(this, 'NOTIFICATION SESSION COUNTER', '' + sessionCounter)
      ),
      tap(() => {
        if (nuuma) {
          return;
        }
        if (!this.ctx.isApp) {this.loginAFIN(documentNumber, password); }
        this.secureShare.save({
          documentNumber: padZeros(documentNumber),
          password
        });
      }),
      tap(state => this.secure.updateTokenPass(state.user.tokenPass)),
      tap(state => this.storeState(state)),
      tap(state => this.runBackgroundLoginJobs(state)),
      timeout(60000),
      catchError(err => this.handleLoginErrors(err))
    );
  }

  private async loginAFIN(user, pass) {
    const token = await this.pensionPlanHelper.getTokenAFIN(user, pass)
      .then((res: any) => res?.token)
      .catch(() => null);
    sessionStorage.setItem('tokenAFIN', token);
  }

  private loginBusiness({ cif, nif, password, }: { cif: string; nif: string; password: string; }) {
    return this.authHttp.businessLogin({ cif, nif, password })
    .pipe(
      map(
        user =>
          ({
            ...this.getState(),
            sessionCounter: this.plusOneSessionCounter(),
            isLogged: true,
            password: this.platform.is('cordova') ? password : null,
            user: {
              ...user,
              userId: this.getUserId(user),
              additionalUserIds: this.getAdditionalUserIds(user)
            }
          } as Auth)
      ),
      tap(({ sessionCounter }) =>
        this.log.d(this, 'NOTIFICATION SESSION COUNTER', '' + sessionCounter)
      ),
      tap(() => {
        this.secureShare.save({
          documentNumber: padZeros(cif),
          nif: padZeros(nif),
          password
        });
      }),
      tap(state => this.storeState(state)),
      tap(state => this.runBackgroundLoginJobs(state)),
      timeout(60000),
      catchError(err => throwError(err.error))
    );
  }

  private loginBiometric(type: AuthType) {
    const { documentNumber } = this.getState().preferences;
    const getPassword = () => this.keychain.get('PASSWORD').catch(() => '');

    return defer(getPassword).pipe(
      switchMap(password => this.login({ documentNumber, password }, type))
    );
  }

  private loginToken(payload) {
    let companies = false;
    if (this.decodeTokenSafe(payload?.token)?.claims?.cif) {
      companies = true;
    }
    return this.logout().pipe(switchMap(() => {
      if (companies === true) {
        this.ctx.updateAccessType('business');
        return this.loginBusinessToken(payload);
      }
      return  this.loginParticularsToken(payload);
    }));
  }

  private loginParticularsToken(payload) {
    const documentNumber = this.decodeTokenSafe(payload?.token)?.claims?.client?.documentNumber;
    const nuumaSim = this.decodeTokenSafe(payload?.token)?.claims?.simulatingUser;
    return this.interceptToken(payload).pipe(switchMap(() => {
      return this.authHttp.getLoginInfo().pipe(
        map(
          user =>
            ({
              ...this.getState(),
              sessionCounter: 0,
              isLogged: !this.isAppFirstLogin() && user.policyAccepted,
              password: null,
              user: {
                ...user,
                nuumaSim
              }
            } as Auth)
        ),
        tap(state => this.secure.updateTokenPass(state.user.tokenPass)),
        tap(state => this.storeState(state)),
        tap(state => this.runBackgroundLoginJobs(state)),
        timeout(60000),
        catchError(err => throwError({...err, documentNumber}))
      );
    }));
  }

  private loginBusinessToken(payload) {
    const documentNumber = this.decodeTokenSafe(payload?.token)?.claims?.cif;
    const nif = this.decodeTokenSafe(payload?.token)?.claims?.nif;
    const nuumaSim = this.decodeTokenSafe(payload?.token)?.claims?.nuuma;
    return this.authHttp.getCompaniesLoginInfo(payload?.token)
    .pipe(
      map(
        user =>
          ({
            ...this.getState(),
            sessionCounter: this.plusOneSessionCounter(),
            isLogged: true,
            password: null,
            user: {
              ...user,
              policyAccepted: true,
              nuumaSim
            }
          } as Auth)
      ),
      tap(({ sessionCounter }) =>
        this.log.d(this, 'NOTIFICATION SESSION COUNTER', '' + sessionCounter)
      ),
      tap(() => {
        this.secureShare.save({
          documentNumber: padZeros(documentNumber),
          nif: padZeros(nif),
        });
      }),
      tap(state => this.storeState(state)),
      tap(state => this.runBackgroundLoginJobs(state)),
      timeout(60000),
      catchError(err => throwError({...err, documentNumber}))
    );
  }

  private loginBusinessCertificateToken(payload: BusinessCertificateLoginPayload) {
    return this.authHttp.businessCertificateLogin(payload?.token)
    .pipe(
      map(
        user => {
          const sessionCounter = ++this.getState().sessionCounter || 0;
          return ({
            ...this.getState(),
            sessionCounter,
            isLogged: true,
            password: null,
            user
          } as Auth);
        },
      ),
      tap(({ sessionCounter }) =>
        this.log.d(this, 'NOTIFICATION SESSION COUNTER', '' + sessionCounter)
      ),
      tap(() => {
        this.secureShare.save({
          documentNumber: padZeros(payload.cif),
          nif: padZeros(payload.nif),
        });
      }),
      tap(state => this.storeState(state)),
      tap(state => this.runBackgroundLoginJobs(state)),
      timeout(60000),
      catchError(err => throwError(err.error))
    );
  }

  private loginAci(payload) {
    const username = payload.username;
    const password = payload.password;
    return this.logout().pipe(switchMap(() => {
      return this.interceptToken(payload).pipe(switchMap(() => {
        return this.authHttp.aciLogin({username, password}).pipe(
          map(
            user =>
              ({
                ...this.getState(),
                sessionCounter: 0,
                isLogged: true,
                password: null,
                user
              } as Auth)
          ),
          tap(state => this.secure.updateTokenPass(state.user.tokenPass)),
          tap(state => this.storeState(state)),
          tap(state => this.runBackgroundLoginJobs(state)),
          timeout(60000),
          catchError(err => throwError({...err, username}))
        );
      }));
    }));
  }

  private softLogout() {
    if (!this.platform.is('cordova')) {
      return this.logout();
    }
    return of(this.getState()).pipe(
      tap(() => {
        if (this.platform.is('cordova')) {
          this.keyboard.hide();
        }
      }),
      tap(() => {
        this.secureShare.clear();
        this.teCuidamos.clear();
        this.profileCtx.clear();
        this.documentCaptureCtx.clear();
        this.passwordRecoveryCtx.clear();
        this.nonRenewalContext.clear();
        this.paymentPlanService.clear();
        this.urlExternal.close();
        this.genesysHelper.clear();
      }),
      map(state => ({ ...state, isLogged: false }))
    );
  }

  private logout() {
    return of(this.getState()).pipe(
      tap(() => {
        if (this.platform.is('cordova')) {
          this.keyboard.hide();
        }
      }),
      tap(() => {
        this.documentCaptureCtx.clear();
        this.teCuidamos.clear();
        this.secureShare.clear();
        this.paymentPlanCtx.clear();
        this.profileCtx.clear();
        this.policiesCtx.clear();
        this.notificationHelper.clear();
        sessionStorage.removeItem(WILLMANAGEMENT_DOCUMENTS);
        this.passwordRecoveryCtx.clear();
        this.paymentPlanService.clear();
        this.genesysHelper.clear();

        this.urlExternal.close();
        this.secure.delete('AUTH__APP_PREFERENCES');
        this.secure.deleteAllEncripted();
        this.patrimonialDataChangeHelper.completeState();
        this.genesysHelper.closeGenesysChat();

      }),
      map(() => this.initialState)
    );
  }

  private setAppAuthenticationType({ type }: SetAuthenticationPayload) {
    return of(this.getState()).pipe(
      map(
        state =>
          ({
            ...state,
            isLogged: true,
            preferences: {
              documentNumber: padZeros(state.user.documentNumber),
              name: state.user ? state.user.name : '',
              nuumaSim: state.user.nuumaSim,
              type
            }
          } as Auth)
      ),
      tap(state => this.storeAppPreferencesAndAuthState(state))
    );
  }

  private interceptToken({ token }) {
    return of(this.getState()).pipe(
      map(
        state =>
          ({
            ...state,
            user: {
              ...state.user,
              token
            }
          } as Auth)
      ),
      tap(state =>  this.state.next(state)),
      tap(state => this.storeState(state))
    );
  }

  private acceptRgpdue() {
    return of(this.getState()).pipe(
      map(
        state =>
          ({
            ...state,
            user: {
              ...state.user,
              policyAccepted: true
            },
            isLogged: true
          } as Auth)
      ),
      tap(state => this.storeState(state))
    );
  }

  private updatePassword({ password }: UpdatePasswordPayload) {
    return of(this.getState()).pipe(
      map(
        state =>
          ({
            ...state,
            password
          } as Auth)
      ),
      tap(state => this.storeAppPreferencesAndAuthState(state))
    );
  }

  private updatePersonalData(data: UpdatePersonalDataPayload) {
    return of(this.getState()).pipe(
      map(state => {
        const { user } = state;
        return {
          ...state,
          user: { ...user, ...data }
        } as Auth;
      }),
      tap(state => this.storeAppPreferencesAndAuthState(state))
    );
  }

  private updatePhone({ phone }: UpdatePhonePayload) {
    return of(this.getState()).pipe(
      map(state => {
        const { user } = state;
        return {
          ...state,
          user: { ...user, phone }
        } as Auth;
      }),
      tap(state => this.storeAppPreferencesAndAuthState(state))
    );
  }

  private updateCompanyPhone({ phone }: UpdatePhonePayload) {
    return of(this.getState()).pipe(
      map(state => {
        const { user } = state;
        const legalRepresentative = { ...user.legalRepresentative, ...{ phone } };
        return {
          ...state,
          user: { ...user, ...{ legalRepresentative } }
        } as Auth;
      }),
      tap(state => this.storeAppPreferencesAndAuthState(state))
    );
  }

  private updateLegalRepresentative(newLegalRepresentative: UpdateLegalRepresentativePayload) {
    return of(this.getState()).pipe(
      map(state => {
        const { user } = state;
        const legalRepresentative = { ...user.legalRepresentative, ...newLegalRepresentative };
        return {
          ...state,
          user: {
            ...user, ... { legalRepresentative }
          }
        } as Auth;
      }),
      tap(state => this.storeAppPreferencesAndAuthState(state))
    );
  }

  private storeState(state: Auth) {
    // If first login on APP, there is no auth type selected yet.
    // We have to wait the user until selects an Auth Type, if cancels, none is saved.
    if (this.isAppFirstLogin()) {
      return Promise.resolve();
    }

    return this.secure.save('AUTH__STATE', state);
  }

  private isAppFirstLogin() {
    return this.platform.is('cordova') && isEmpty(this.getState().preferences);
  }

  private async storeAppPreferencesAndAuthState(state: Auth) {
    this.storePassword(state);
    this.secure.saveNotSecure('AUTH__APP_PREFERENCES', state.preferences);
    this.secure.save('AUTH__STATE', state);
  }

  private storePassword(state: Auth) {
    if (this.isBiometric(state)) {
      // State defined as any on purpose, password property should not be available. Hence is not
      // present on Typescript model. Its only use is for storing it on the keychain.
      const password = (state as any).password;
      this.keychain.set('PASSWORD', password).catch(() => {});
    }
  }

  private handleSdmLoginErrors(response: HttpErrorResponse): Observable<never> {
    const error = response?.error?.error;
    const remoteHttpStatus = response?.status;
    const remoteExtradata = error?.error_detail;
    const remoteErrorCode = error?.error_code;
    if (!remoteHttpStatus) {
      return throwError({});
    }

    return throwError({ remoteExtradata, remoteHttpStatus, remoteErrorCode });
  }

  private handleLoginErrors({ error }: HttpErrorResponse): Observable<never> {
    // Login
    const errorExtradata = error && error.extraData;
    // Remote Server Error
    const remoteHttpStatus =
      errorExtradata && errorExtradata.remoteServerStatusCode;
    const remoteErrorBody = errorExtradata && errorExtradata.remoteServerBody;
    const remoteExtradata = remoteErrorBody && remoteErrorBody.extraData;
    if (!remoteHttpStatus) {
      return throwError({});
    }

    return throwError({ remoteExtradata, remoteHttpStatus });
  }

  private runBackgroundLoginJobs(state: Auth) {
    this.getTokenAvailable$()
      .pipe(
        tap(() => this.teCuidamos.loginTeCuidamos().subscribe()),
        tap(() => this.menu.updateMenuState()),
        tap(() => this.paymentPlanCtx.setInitialState()),
        tap(() => this.storePassword(state)),
        tap(() => {
          if (this.isAppFirstLogin()) this.analyticsContext.newRepeat = 'yes';
        })
      )
      .subscribe(
        () => {},
        () => {}
      );
  }

  private getTokenAvailable$() {
    return this.getState$().pipe(
      pluck('user', 'token'),
      filter(token => !!token),
      first()
    );
  }

  /** @return nuuma if is logSim */
  private getNuumaSim(nuumaSim) {
    // First login
    if (nuumaSim) {
      return nuumaSim;
    }
    // Next logins
    const { preferences } = this.getState();
    const storedNuumaSim = preferences && preferences.nuumaSim;
    return storedNuumaSim;
  }

  private isBiometric({ preferences }: Auth) {
    const type = preferences.type || '';
    return type === 'FACEID_APP' || type === 'FINGERPRINT_APP';
  }

  private getUserId(user: User){
    try {
      return this.jwt.decodeToken(user?.token)?.claims?.client?.userId;
    } catch (error) {
      this.log.w(this, error?.message);
      return '';
    }
  }

  private getAdditionalUserIds(user: User){
    try {
      return this.jwt.decodeToken(user?.token)?.claims?.client?.additionalUserIds;
    } catch (error) {
      this.log.w(this, error?.message);
      return [];
    }
  }

  private initializeInactivityLogout() {
    const clicks$ = fromEvent(document, 'click');
    const keys$ = fromEvent(document, 'keydown');
    const mouse$ = fromEvent(document, 'mousemove');
    let idleSeconds: number;
    const throttleSeconds = 60;
    let wasLoggedBeforeInactivityLogout: boolean;

    from(this.mmobileHelper.isReady()).pipe(
      tap(() => {
        idleSeconds = this.mmobileHelper.getCustomConfigData('IDLE_TIME') || 900;
      }),
      switchMap(() => {
        return merge(clicks$, keys$, mouse$).pipe(
          throttleTime(throttleSeconds * 1000),
          switchMapTo(timer(idleSeconds * 1000)),
          tap(() => this.log.i(this, 'User idle timeout, logout of app')),
          switchMap(() => {
            const { isLogged } = this.getState();
            wasLoggedBeforeInactivityLogout = isLogged;
            if (isLogged) {
              return this.dispatch$(new Logout({ softLogout: true }));
            }
            return of(null);
          })
        );
      })
    ).subscribe({
      next: () => {
        if (wasLoggedBeforeInactivityLogout) {
          const isParticular = this.ctx.isAccessTypeParticular;
          this.nav.goLoginPage(isParticular, { queryParams: { manual: true } });
        }
      }
    });
  }

  private async checkSdmDocumentNumberList(documentNumber: string) {
    const regexConfig = JSON.parse((await this.firebaseConfig.getConfigValue('sdmRegexConfig')).asString());
    const sdmDocumentNumberRegex = new RegExp(regexConfig?.sdmRegex, 'i');
    return sdmDocumentNumberRegex.test(padZeros(documentNumber, 9));
  }

  private plusOneSessionCounter() {
    return ++this.getState().sessionCounter || 0;
  }
}
